import React, { useEffect, useState } from "react";
import { generateObjectId } from "../../../utils/objectIdUtils";
import SectionTabs from "./SectionTabs";
import NewFormGridLayout from "./NewFormGridLayout";
import {
  addTabIdToLayout,
  addTabIdToLayoutIfMissing,
  assignTabIdToField,
  flattenFieldsToMap,
  groupLayoutByTabs,
  removeTabIdFromLayout,
  updateGlobalOuterLayoutOnTabChange
} from "./sectionUtils";
import { toastError } from "../../../utils/toastUtils";
import Toggle from "../../../components/FormComponents/Toggle";
import { useModalContext } from "../../../context/ModalContext";

function SectionLayout({ sectionData, onChangeSection }) {
  const [activeTab, setActiveTab] = useState(sectionData?.section_tabs?.[0]?.id || null);
  const [sectionTabLayout, setSectionTabLayout] = useState(null);
  const [fieldMap, setFieldMap] = useState({});
  const { getUserConfirmation } = useModalContext();

  const handleFormType = async () => {
    if (sectionData?.is_section_tab_layout) {
      const confirmed = await getUserConfirmation(
        `Are you sure you want to disable the tab layout?<br/> <b>This action cannot be Undone</b>.`
      );

      if (!confirmed) return;

      const updatedOuterLayout = removeTabIdFromLayout(sectionData.desktop_layout.outer_layout);
      onChangeSection({
        ...sectionData,
        section_tabs: [],
        is_section_tab_layout: false,
        desktop_layout: {
          ...sectionData.desktop_layout,
          outer_layout: updatedOuterLayout
        }
      });
      setActiveTab(null);
    } else {
      let newSectionTabs = sectionData.section_tabs || [];

      const defaultSection = {
        id: generateObjectId(),
        name: "Section 1"
      };
      newSectionTabs = [defaultSection];
      const updatedOuterLayout = addTabIdToLayout(sectionData.desktop_layout.outer_layout, defaultSection.id);
      const fullLayout = {
        ...sectionData.desktop_layout,
        outer_layout: updatedOuterLayout
      };
      const sectionLayout = groupLayoutByTabs(fullLayout, fieldMap);
      setSectionTabLayout(sectionLayout);
      onChangeSection({
        ...sectionData,
        is_section_tab_layout: true,
        section_tabs: newSectionTabs,
        desktop_layout: fullLayout
      });
      setActiveTab(defaultSection.id);
    }
  };

  const handleTabChange = (newSections) => {
    onChangeSection({ ...sectionData, section_tabs: newSections });
  };

  const handleTabClick = (tabId) => {
    if (!sectionTabLayout[tabId]) {
      setSectionTabLayout({
        ...sectionTabLayout,
        [tabId]: {
          outer_layout: []
        }
      });
    }
    setActiveTab(tabId);
  };

  const handleChangeLayout = (newLayout) => {
    if (sectionData?.is_section_tab_layout) {
      const updatedOuterLayout = updateGlobalOuterLayoutOnTabChange(
        sectionData.desktop_layout.outer_layout,
        newLayout.outer_layout,
        activeTab
      );
      onChangeSection({
        ...sectionData,
        desktop_layout: {
          ...sectionData.desktop_layout,
          ...newLayout,
          outer_layout: updatedOuterLayout
        }
      });
      setSectionTabLayout({
        ...sectionTabLayout,
        [activeTab]: newLayout
      });
    } else {
      onChangeSection({ ...sectionData, desktop_layout: newLayout });
    }
  };

  const handleChangeFieldTab = (e, field) => {
    const updatedOuterLayout = assignTabIdToField(sectionData.desktop_layout.outer_layout, e.target.value, field.id);
    const fullLayout = {
      ...sectionData.desktop_layout,
      outer_layout: updatedOuterLayout
    };
    const sectionLayout = groupLayoutByTabs(fullLayout, fieldMap);
    setSectionTabLayout(sectionLayout);
    onChangeSection({
      ...sectionData,
      desktop_layout: fullLayout
    });
  };

  const handleDeleteTab = (tabId) => {
    const isTabInLayout = sectionData.desktop_layout.outer_layout.some((item) => item.tab_id === tabId);

    if (isTabInLayout) {
      toastError("This section cannot be deleted as it contains form fields.");
      return;
    }

    const updatedSections = sectionData.section_tabs.filter((section) => section.id !== tabId);

    onChangeSection({
      ...sectionData,
      section_tabs: updatedSections
    });

    if (activeTab === tabId) {
      setActiveTab(updatedSections[0]?.id || null);
    }
  };

  useEffect(() => {
    const fieldMap = flattenFieldsToMap(sectionData?.fields || []);
    if (sectionData?.is_section_tab_layout) {
      const firstTabId = sectionData.section_tabs[0].id;
      const { newOuterLayout, layoutChanged } = addTabIdToLayoutIfMissing(
        sectionData.desktop_layout.outer_layout,
        firstTabId
      );
      const fullLayout = {
        ...sectionData.desktop_layout,
        outer_layout: newOuterLayout
      };
      if (layoutChanged) {
        onChangeSection({
          ...sectionData,
          desktop_layout: fullLayout
        });
      }

      const sectionLayout = groupLayoutByTabs(fullLayout, fieldMap);
      setSectionTabLayout(sectionLayout);
    }
    setFieldMap(fieldMap);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (Object.keys(fieldMap).length === 0) return;
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div className="flex_column gap_2">
        <div className="ml_auto">
          <div className="flex_row align_center">
            <label className="mr_2">Section Layout</label>
            <Toggle value={sectionData?.is_section_tab_layout || false} onChange={handleFormType} />
          </div>
        </div>
        <SectionTabs
          activeTab={activeTab}
          onTabClick={handleTabClick}
          sectionsTabs={sectionData?.section_tabs || []}
          onChange={handleTabChange}
          onDeleteTab={handleDeleteTab}
        />
      </div>
      <div style={{ height: "calc(100vh - 306px)" }} className="flex_column gap_2 overflow_scroll">
        <NewFormGridLayout
          layout={
            sectionData?.is_section_tab_layout
              ? sectionTabLayout[activeTab] || { outer_layout: [] }
              : sectionData.desktop_layout
          }
          fieldMap={fieldMap}
          renderHoverActions={(field, parentId) => {
            if (parentId !== "outer_layout") {
              return null;
            }
            return (
              <select
                className="px-2 py-1 text-sm border rounded"
                onChange={(e) => handleChangeFieldTab(e, field, parentId)}
                defaultValue=""
              >
                <option value="" disabled>
                  Select Tab
                </option>
                {(sectionData?.section_tabs || []).map((tab) => {
                  if (tab.id === activeTab) {
                    return null;
                  }
                  return (
                    <option key={tab.id} value={tab.id}>
                      {tab.name}
                    </option>
                  );
                })}
              </select>
            );
          }}
          onChange={handleChangeLayout}
        />
      </div>
    </div>
  );
}

export default SectionLayout;
